import { type ProductType, productService, toNumber } from './product.service'

/**
 * Vista del catálogo para cotizar. Lo que se cotiza sale de `products`, donde
 * conviven servicios y productos; antes vivía en `parameters` con
 * type = 'servicio' y por eso este módulo existe: las cotizaciones y las cuentas
 * de cobro sólo necesitan nombre, precio y alcance, no la ficha entera.
 *
 * Para gestionar el catálogo (variantes, existencias, imágenes) está
 * `product.service.ts`; esto es sólo el escaparate desde el que se elige.
 */
export interface ServiceItemType {
  id: number
  key: string
  name: string
  description: string
  /** Precio de referencia que se propone al añadirlo a una cotización. */
  price: number
  /** Alcance / entregables, una línea por ítem (puede venir como string u objeto desde el backend). */
  details: (string | Record<string, unknown>)[]
  unit: string
  active: boolean
  order: number
}

function toService(product: ProductType): ServiceItemType {
  return {
    id: product.id,
    key: product.key,
    name: product.name,
    description: product.description ?? '',
    price: toNumber(product.price),
    details: product.details ?? [],
    unit: product.unit,
    active: product.status === 'published',
    order: product.order ?? 0
  }
}

export interface ServicePayloadType {
  name: string
  key?: string
  description?: string
  price?: number
  details?: string[]
  unit?: string
  active?: boolean
  order?: number
}

export const serviceCatalogService = {
  async list() {
    // El catálogo completo de una vez: se usa para elegir, no para paginar.
    const result = await productService.list({ limit: 200 })
    return result.items
      .filter(item => item.status !== 'archived')
      .map(toService)
      .sort((a, b) => a.order - b.order || a.name.localeCompare(b.name))
  },

  /**
   * Crea desde el diálogo rápido de una cotización. Nace como servicio porque es
   * lo que se está cotizando; si luego resulta ser un producto, se cambia en su
   * ficha, que es donde están las existencias y las variantes.
   */
  async create(payload: ServicePayloadType) {
    const product = await productService.create({
      kind: 'service',
      name: payload.name,
      key: payload.key,
      description: payload.description ?? '',
      price: payload.price ?? 0,
      details: payload.details ?? [],
      unit: payload.unit ?? 'servicio',
      status: payload.active === false ? 'draft' : 'published',
      order: payload.order ?? 0
    })
    return toService(product)
  },

  async update(id: number, payload: ServicePayloadType) {
    const product = await productService.update(id, {
      name: payload.name,
      description: payload.description ?? '',
      price: payload.price ?? 0,
      details: payload.details ?? [],
      unit: payload.unit ?? 'servicio',
      status: payload.active === false ? 'draft' : 'published',
      order: payload.order ?? 0
    })
    return toService(product)
  },

  async delete(id: number) {
    return productService.delete(id)
  }
}
